import React, { useEffect, useState } from 'react';

// ak-stage-email has no fields to submit — the flow only advances once the
// person clicks the link in their inbox, which (once the Authentik email
// template is pointed at our own domain — see VerifyEmailCallback.jsx)
// lands on /verify-email?token=..., a separate route/tab, not this one.
//
// onResend: registration only — lets the person request a fresh link
// without restarting registration. Added after a real @bescom.co.in
// delivery delay outlived the original link's TTL (see registration.js's
// REGISTRATION_WAIT_WINDOW_SECONDS header). undefined for any other caller
// (there are none today — self-service add-email doesn't use this
// component), which just hides the button.
const RESEND_COOLDOWN_SECONDS = 30;

const btnSecondary = {
  width: '100%',
  padding: '12px',
  background: 'none',
  border: '1px solid #D3D1C7',
  color: '#666',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  marginTop: '16px',
};

export default function EmailPendingStep({ email, onResend, dark }) {
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState(null);
  const [resent, setResent] = useState(false);

  const headingColor = dark ? '#fff' : '#1a1a1a';
  const bodyColor = dark ? 'rgba(255, 255, 255, 0.75)' : '#666';
  const successColor = dark ? '#7FE0C3' : '#0F6E56';
  const secondaryBtnStyle = dark
    ? { ...btnSecondary, border: '1px solid rgba(255, 255, 255, 0.35)', color: 'rgba(255, 255, 255, 0.75)' }
    : btnSecondary;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function handleResend() {
    setResending(true);
    setResendError(null);
    try {
      const ok = await onResend();
      if (ok) {
        setResent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setResendError('Could not resend the confirmation email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '40px', marginBottom: '16px' }}>📧</div>
      <p style={{ color: headingColor, fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
        Check your inbox
      </p>
      <p style={{ color: bodyColor, fontSize: '16px' }}>
        We've sent a verification link{email ? ` to ${email}` : ''}. Click it to continue —
        this page will pick up automatically once you do.
      </p>

      {resendError && (
        <div style={{
          background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
          borderRadius: '8px', fontSize: '15px', marginTop: '16px',
        }}>
          {resendError}
        </div>
      )}

      {resent && !resendError && (
        <p style={{ color: successColor, fontSize: '15px', marginTop: '16px' }}>
          A new link is on its way.
        </p>
      )}

      {onResend && (
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || resendCooldown > 0}
          style={{
            ...secondaryBtnStyle,
            opacity: (resending || resendCooldown > 0) ? 0.6 : 1,
            cursor: (resending || resendCooldown > 0) ? 'not-allowed' : 'pointer',
          }}
        >
          {resending ? 'Resending…' : resendCooldown > 0 ? `Resend link in ${resendCooldown}s` : "Didn't get it? Resend link"}
        </button>
      )}
    </div>
  );
}
